<script>
/**
 * Cabeçalho de página padronizado (piloto do novo padrão visual — ver levantamento de layout).
 *
 * Substitui as ~4 variações de `.page-title-box` + breadcrumb escritos à mão em cada view.
 * No celular: breadcrumb enxuto (só o último nível), título, subtítulo e as ações quebrando
 * linha abaixo do título.
 *
 * Uso:
 *   <StPageHeader title="Minhas Metas" subtitle="..." :breadcrumb="[{ text: 'Minhas Metas' }]">
 *     <template #actions><b-button>...</b-button></template>
 *     <template #meta>Atualizado em ...</template>
 *   </StPageHeader>
 */
export default {
  name: "StPageHeader",
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: "" },
    // [{ text, to? }] — o "Início" é sempre inserido antes.
    breadcrumb: { type: Array, default: () => [] },
  },
};
</script>

<template>
  <header class="st-page-header">
    <nav aria-label="Você está em">
      <ol class="breadcrumb st-page-header-crumbs">
        <li class="breadcrumb-item">
          <router-link to="/" aria-label="Início"><i class="bx bx-home-alt" aria-hidden="true"></i></router-link>
        </li>
        <li
          v-for="(item, i) in breadcrumb"
          :key="i"
          class="breadcrumb-item"
          :class="{ 'd-none d-sm-block': i < breadcrumb.length - 1 }"
          :aria-current="i === breadcrumb.length - 1 ? 'page' : null"
        >
          <router-link v-if="item.to" :to="item.to">{{ item.text }}</router-link>
          <template v-else>{{ item.text }}</template>
        </li>
      </ol>
    </nav>

    <div class="st-page-header-row">
      <div class="st-page-header-text">
        <h1 class="st-page-header-title">{{ title }}</h1>
        <p v-if="subtitle" class="st-page-header-subtitle">{{ subtitle }}</p>
        <div v-if="$slots.meta" class="st-page-header-meta"><slot name="meta"></slot></div>
      </div>
      <div v-if="$slots.actions" class="st-page-header-actions">
        <slot name="actions"></slot>
      </div>
    </div>
  </header>
</template>

<style scoped>
.st-page-header {
  margin-bottom: 1.25rem;
}
.st-page-header-crumbs {
  margin-bottom: 0.5rem;
  padding: 0;
  background: transparent;
  font-size: 0.8125rem;
}
.st-page-header-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.75rem;
}
.st-page-header-text {
  min-width: 0;
  flex: 1 1 16rem;
}
/* Mesma escala do .page-title-box (design/custom/structure/_page-head.scss). */
.st-page-header-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: -0.025em;
  color: var(--st-heading);
}
.st-page-header-subtitle {
  margin: 0.25rem 0 0;
  color: var(--st-text-muted);
  font-size: 0.9375rem;
}
.st-page-header-meta {
  margin-top: 0.375rem;
  font-size: 0.8125rem;
  color: var(--st-text-muted);
}
.st-page-header-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
@media (max-width: 575.98px) {
  .st-page-header-title {
    font-size: 1.1875rem;
  }
  .st-page-header-actions {
    width: 100%;
  }
  .st-page-header-actions > * {
    flex: 1 1 auto;
  }
}
</style>
